import { useEffect, useState } from 'react'
import { api } from '@aurora/convex/convex/_generated/api'
import { auroraStore, useAuroraStore, type ViewKey } from './store/auroraStore'
import { activeChildId } from './store/selectors'
import { ConvexSync, getToken, setToken } from './sync/ConvexSync'
import { authReactClient, localAuth } from './sync/auth'
import { startHashRouter } from './sync/hashRouter'
import { useTheme } from './hooks/useTheme'
import { AppShell } from './components/AppShell'
import { Welcome } from './views/Welcome'
import { ThreadView } from './views/ThreadView'
import { TimelineView } from './views/TimelineView'
import { TodayView } from './views/TodayView'
import { StoryView } from './views/StoryView'
import { PlayroomView } from './views/PlayroomView'
import { GameView } from './views/GameView'
import { LibraryView } from './views/LibraryView'
import { ChildView } from './views/ChildView'
import { KidMode } from './views/KidMode'
import { CaptureLayer } from './components/capture/CaptureLayer'
import { ToastLayer } from './components/ToastLayer'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { isLocalDemo, seedLocalDemo } from './lib/localDemo'

const CONVEX_URL = import.meta.env.VITE_CONVEX_URL ?? ''

const LABELS: Partial<Record<ViewKey, string>> = {
  timeline: 'The timeline',
  thread: 'The thread',
  today: 'Today',
  story: 'Progress',
  playroom: 'The playroom',
  game: 'This game',
  library: 'The library',
  child: 'Her page',
}

/**
 * For a Convex Auth session: has this parent picked or joined a family yet?
 * Retries briefly because the react client authenticates a beat after the
 * JWT lands. Stays null while unknown (and always for legacy or demo
 * sessions, where the question never applies).
 */
function useNeedsFamily(enabled: boolean): boolean | null {
  const [needsFamily, setNeedsFamily] = useState<boolean | null>(null)
  useEffect(() => {
    if (!enabled || !authReactClient) return
    let cancelled = false
    void (async () => {
      for (let i = 0; i < 5 && !cancelled; i++) {
        const me = await authReactClient.query(api.authApi.me, {}).catch(() => null)
        if (cancelled) return
        if (me) {
          setNeedsFamily(me.needsFamily)
          return
        }
        await new Promise((r) => setTimeout(r, 400))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [enabled])
  return needsFamily
}

/**
 * The thread is the app; the other views are lenses on it.
 *
 * Kid Mode renders outside the shell — no navigation, nothing to wander into.
 */
export function App() {
  const view = useAuroraStore((s: any) => s.ui.view as ViewKey)
  const childId = useAuroraStore(activeChildId)
  const token = getToken()
  // Signed in the new way: a Convex Auth JWT in storage instead of a token.
  const hasAuth = localAuth.useLocalAuth()
  const needsFamily = useNeedsFamily(!token && hasAuth)

  useTheme()
  useEffect(() => startHashRouter(), [])

  // Rehydrate the local demo on reload — it lives only in memory. `?demo=1`
  // enters it directly, which is also what headless screenshot runs use.
  useEffect(() => {
    const wantsDemo =
      isLocalDemo() || new URLSearchParams(window.location.search).get('demo') === '1'
    if (wantsDemo && !childId) {
      if (!getToken()) setToken('local-demo')
      seedLocalDemo()
    }
  }, [childId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Number keys switch views only when they are not someone's input: a
      // field, a picker, or a game being previewed in the page, where a digit
      // is an answer, not a request to leave.
      const el = e.target as HTMLElement | null
      const typing = el?.closest?.('input, textarea, select, [contenteditable], .gp-preview')
      if (typing || e.repeat || e.isComposing || e.metaKey || e.ctrlKey || e.altKey) return
      const map: Record<string, ViewKey> = {
        '1': 'thread', '2': 'timeline', '3': 'playroom', '4': 'story', '5': 'library', '6': 'child',
      }
      const s = auroraStore.getState() as any
      if (map[e.key] && s.ui.view !== 'kid') s.setView(map[e.key])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!token && !hasAuth) return <Welcome convexUrl={CONVEX_URL} />
  if (!token && needsFamily) return <Welcome convexUrl={CONVEX_URL} needsFamily />

  if (view === 'kid') {
    return (
      <>
        <ConvexSync convexUrl={CONVEX_URL} />
        <KidMode />
      </>
    )
  }

  return (
    <>
      {CONVEX_URL && <ConvexSync convexUrl={CONVEX_URL} />}
      <AppShell>
        {/* Keyed so a crashed lens recovers when you navigate away from it. */}
        <ErrorBoundary key={view} label={LABELS[view]}>
          {!childId ? (
            <Welcome convexUrl={CONVEX_URL} needsChild />
          ) : view === 'timeline' ? (
            <TimelineView />
          ) : view === 'thread' ? (
            <ThreadView />
          ) : view === 'today' ? (
            <TodayView />
          ) : view === 'story' ? (
            <StoryView />
          ) : view === 'playroom' ? (
            <PlayroomView />
          ) : view === 'game' ? (
            <GameView />
          ) : view === 'child' ? (
            <ChildView />
          ) : (
            <LibraryView />
          )}
        </ErrorBoundary>
      </AppShell>
      {/* Capture floats over every lens but the two with their own composer,
          where it would sit on the send button. */}
      {view !== 'thread' && view !== 'game' && <CaptureLayer />}
      <ToastLayer />
    </>
  )
}
